import React, { useCallback } from "react";
import { useCurrentSession } from "@ats/src/context/CurrentSessionContext";
import { usePlanFeatures } from "@shared/queryHooks/useBilling";
import { useAllInvites } from "@shared/queryHooks/useInvite";
import { getModalDisplayName } from "@shared/lib/planHelpers";

interface AddActiveUserGateResult {
  shouldProceed: boolean;
  modalType?: "upgrade" | "freeTrial" | "reactivate";
  errorMessage?: string;
  modalData?: {
    currentPlanName?: string;
    userLimit?: number;
    nextPlanName?: string;
    activeUserCount?: number;
    hasPendingInvites?: boolean;
    hasMultipleActiveUsers?: boolean;
    recommendedPlanName?: string;
  };
}

export function useAddActiveUserGate() {
  const { currentOrganization } = useCurrentSession();
  const { data: planFeaturesData, isLoading: isLoadingPlanFeatures } = usePlanFeatures({
    refetchOnWindowFocus: false,
  });
  const { data: invitesData, isLoading: isLoadingInvites } = useAllInvites();

  React.useEffect(() => {
    window.logger("%c[useAddActiveUserGate] useEffect - planFeatures state", "color: #4CAF50", {
      isLoadingPlanFeatures,
      isLoadingInvites,
      hasPlanFeaturesData: !!planFeaturesData,
      hasPlans: !!planFeaturesData?.plans,
      plansLength: planFeaturesData?.plans?.length,
      currentOrganization: !!currentOrganization,
      hasInvitesData: !!invitesData,
    });
  }, [isLoadingPlanFeatures, isLoadingInvites, planFeaturesData, invitesData, currentOrganization]);

  const checkGate = useCallback((): AddActiveUserGateResult => {
    if (!currentOrganization) {
      window.logger("%c[useAddActiveUserGate] Missing currentOrganization", "color: #f21919");
      return {
        shouldProceed: false,
        errorMessage:
          "Unable to load organization information. Please refresh the page and try again.",
      };
    }

    if (isLoadingPlanFeatures || isLoadingInvites) {
      window.logger(
        "%c[useAddActiveUserGate] Plan features or invites still loading",
        "color: #FF9800",
      );
      return {
        shouldProceed: false,
        errorMessage: "Loading plan information. Please wait a moment and try again.",
      };
    }

    if (!planFeaturesData?.plans) {
      window.logger(
        "%c[useAddActiveUserGate] Missing plan features data after loading",
        "color: #f21919",
        { planFeaturesData },
      );
      return {
        shouldProceed: false,
        errorMessage: "Unable to load plan information. Please refresh the page and try again.",
      };
    }

    const { plan } = currentOrganization;
    const plans = planFeaturesData.plans;

    // Check if at user limit
    const currentPlanData = plans.find((p) => p.plan === plan);
    if (!currentPlanData) {
      console.warn("Could not find current plan data");
      return { shouldProceed: true };
    }
    window.logger("%c[useAddActiveUserGate] Current plan data", "color: #4CAF50", {
      currentPlanData,
    });

    const activeUserCount = currentOrganization.organizationUsers.filter(
      (orgUser) => orgUser.isActive,
    ).length;
    const pendingInviteCount = invitesData?.items
      ? invitesData.items.filter((invite) => invite.status === "status_pending").length
      : 0;
    const totalActiveUsers = activeUserCount + pendingInviteCount;

    const hasPendingInvites = pendingInviteCount > 0;
    const hasMultipleActiveUsers = activeUserCount > 1;

    if (currentPlanData.userLimit === "unlimited") {
      window.logger("%c[useAddActiveUserGate] Unlimited user limit - proceeding", "color: #4CAF50");
      return { shouldProceed: true };
    }

    window.logger("%c[useAddActiveUserGate] Initial counts", "color: #9C27B0", {
      activeUserCount,
      pendingInviteCount,
      totalActiveUsers,
      currentPlan: currentPlanData.plan,
      currentPlanLimit: currentPlanData.userLimit,
      currentPlanLimitType: typeof currentPlanData.userLimit,
      allInviteStatuses: invitesData?.items?.map((invite) => invite.status) || [],
      pendingInvites:
        invitesData?.items?.filter((invite) => invite.status === "status_pending") || [],
    });

    if (totalActiveUsers >= currentPlanData.userLimit) {
      if (currentOrganization.eligibleForFreeTrial) {
        return {
          shouldProceed: false,
          modalType: "freeTrial",
        };
      }

      window.logger("%c[useAddActiveUserGate] AT LIMIT - Finding next plan", "color: #FF5722", {
        totalActiveUsers,
        currentPlanLimit: currentPlanData.userLimit,
        currentPlanLimitType: typeof currentPlanData.userLimit,
        currentPlan: currentPlanData.plan,
        comparison: `${totalActiveUsers} >= ${currentPlanData.userLimit}`,
        shouldTrigger: totalActiveUsers >= currentPlanData.userLimit,
        allPlans: plans.map((p) => ({
          plan: p.plan,
          userLimit: p.userLimit,
          legacyPlan: p.legacyPlan,
        })),
      });

      const filteredPlans = plans.filter(
        (p) =>
          !p.legacyPlan && (p.userLimit > currentPlanData.userLimit || p.userLimit === "unlimited"),
      );

      window.logger("%c[useAddActiveUserGate] Filtered plans", "color: #2196F3", {
        filteredPlans: filteredPlans.map((p) => ({
          plan: p.plan,
          userLimit: p.userLimit,
          legacyPlan: p.legacyPlan,
        })),
      });

      const nextPlan = filteredPlans.sort((a, b) => a.userLimit - b.userLimit)[0];

      window.logger("%c[useAddActiveUserGate] Selected next plan", "color: #2196F3", {
        nextPlan: nextPlan ? { plan: nextPlan.plan, userLimit: nextPlan.userLimit } : null,
      });

      return {
        shouldProceed: false,
        modalType: "upgrade",
        modalData: {
          currentPlanName: getModalDisplayName(currentPlanData.plan),
          userLimit: currentPlanData.userLimit,
          ...(nextPlan ? { nextPlanName: getModalDisplayName(nextPlan.plan) } : {}),
          activeUserCount: totalActiveUsers,
          hasPendingInvites,
          hasMultipleActiveUsers,
        },
      };
    }

    window.logger("%c[useAddActiveUserGate] NOT at limit - proceeding", "color: #4CAF50", {
      totalActiveUsers,
      currentPlanLimit: currentPlanData.userLimit,
      currentPlanLimitType: typeof currentPlanData.userLimit,
      comparison: `${totalActiveUsers} < ${currentPlanData.userLimit}`,
      shouldProceed: totalActiveUsers < currentPlanData.userLimit,
    });

    return { shouldProceed: true };
  }, [currentOrganization, planFeaturesData, invitesData, isLoadingPlanFeatures, isLoadingInvites]);

  return {
    checkGate,
    isLoading: isLoadingPlanFeatures || isLoadingInvites,
  };
}
